import { createTranslationRef } from '@backstage/frontend-plugin-api';

export const accessTranslationRef = createTranslationRef({
  id: 'plugin-access',
  messages: {
    common: {
      access: 'Access',
      bindings: 'Assignments',
      roles: 'Access Roles',
      reconciler: 'Access Synchronization',
      auditLog: 'Audit Log',
      accessManagement: 'Access',
      name: 'Name',
      description: 'Description',
      actions: 'Actions',
      close: 'Close',
      cancel: 'Cancel',
      save: 'Save',
      edit: 'Edit',
      delete: 'Delete',
      refresh: 'Refresh',
      kind: 'Kind',
      role: 'Role',
      status: 'Status',
      message: 'Message',
      payload: 'Payload',
      date: 'Date',
      user: 'User',
      action: 'Action',
      subjects: 'Subjects',
      target: 'Target',
      task: 'Task',
      owner: 'Owner',
      type: 'Type',
      externalRef: 'External Ref',
      currentResource: 'Current resource',
      notSet: 'Not set',
      empty: '—',
      yes: 'Yes',
      no: 'No',
      created: 'Created',
      started: 'Started',
      finished: 'Finished',
      duration: 'Duration',
      before: 'Before',
      after: 'After',
      raw: 'Raw',
      overview: 'Overview',
      loading: 'Loading...',
      time: 'Time',
      view: 'View',
      diff: 'Diff',
    },
    page: {
      headerTitle: 'Access',
      headerSubtitle:
        'Manage access roles and assignments stored in the database and synchronized with connected systems.',
      reconcilerTitle: 'Access Synchronization',
      reconcilerDescription:
        'Run access synchronization and track how assigned access is applied in connected systems.',
      reconcilerDeniedTitle:
        'You do not have access to synchronization history',
      reconcilerDeniedDescription:
        'Contact an administrator to request the permission required to read access synchronization history.',
      runReconcile: 'Run Synchronization',
      cleanTables: 'Clean Tables',
      cleanTablesConfirm:
        'Clean access synchronization tables (runs, resource states, materializations, journal)? This action cannot be undone.',
      cleanTablesSuccess:
        'Access synchronization tables cleaned: runs {{runs}}, states {{resourceStates}}, materializations {{materializations}}, journal {{journal}}.',
      contentDescription:
        'Manage access roles and assignments stored in the database and synchronized with connected systems.',
      rbacDescription:
        'Configure who can perform actions inside ADP: view templates, use plugin features, and manage portal objects. Use this section for cases that cannot be configured through access assignments.',
      rbacDeniedTitle: 'You do not have access to ADP permissions',
      rbacDeniedDescription:
        'Contact an administrator to request the permission required to configure actions inside ADP.',
      readOnlyTitle: 'Read-only view',
      readOnlyDescription:
        'You can view this resource, but actions that change it are not available to you.',
      rolesDeniedTitle: 'You do not have access to access roles',
      rolesDeniedDescription:
        'Contact an administrator to request the permission required to view access roles.',
      roleEditDeniedTitle: 'You do not have access to edit this access role',
      roleEditDeniedDescription:
        'This link only allows users with edit permission to change an access role.',
      roleCreateDeniedTitle:
        'You do not have access to create this access role',
      roleCreateDeniedDescription:
        'This link only allows users with create permission to add an access role.',
      bindingsDeniedTitle: 'You do not have access to assignments',
      bindingsDeniedDescription:
        'Contact an administrator to request the permission required to view assignments.',
      bindingEditDeniedTitle: 'You do not have access to edit this assignment',
      bindingEditDeniedDescription:
        'This link only allows users with edit permission to change an assignment.',
      bindingCreateDeniedTitle:
        'You do not have access to create this assignment',
      bindingCreateDeniedDescription:
        'This link only allows users with create permission to add an assignment.',
      auditDeniedTitle: 'You do not have access to the audit log',
      auditDeniedDescription:
        'Contact an administrator to request the permission required to view the audit log.',
    },
    roles: {
      cardTitle: 'Access Roles',
      description:
        'Create reusable roles for one target scope—components, Kubernetes clusters, or projects—and assign them to users and groups for specific resources.',
      showing: 'Showing {{value}} roles',
      createBuiltInRoles: 'Create built in Roles',
      newRole: 'New Role',
      editRole: 'Edit access role',
      createRole: 'Create access role',
      saveRole: 'Save Role',
      deleteRole: 'Delete Role',
      builtInProcessed:
        'Built in roles processed. Created: {{createdCount}}. Skipped existing: {{skippedCount}}.',
      builtInCreatedLocally: 'Built in role created locally',
      componentGroup: 'Components · GitFlic',
      clusterGroup: 'Kubernetes clusters · Bootsman',
      projectGroup: 'Projects · Bootsman',
      mixedGroup: 'Mixed · Requires review',
      emptyGroup: 'No roles in this scope.',
      accessLevel: 'Access level',
      systemRoleName: 'Role name in system',
      backstageRole: 'Backstage role',
      scope: 'Target scope',
      componentScope: 'Component',
      clusterScope: 'Kubernetes cluster',
      projectScope: 'Project',
      scopeHint: 'Choose exactly one target scope.',
      scopeRequired: 'Choose a target scope before saving the role.',
      invalidPermissions:
        'Choose exactly one valid access level for the selected scope.',
      gitflic: 'Gitflic',
      cluster: 'Cluster',
      project: 'Project',
      backstageRoleEntityRef: 'Backstage Role Entity Ref',
      gitflicRole: 'Gitflic Role',
      bootsmanClusterRole: 'Bootsman Cluster Role',
      bootsmanProjectRole: 'Bootsman Project Role',
      loadBackstageRolesError: 'Failed to load backstage roles: {{status}}',
      invalidName: 'Access role {{error}}',
      updated: 'Access role {{name}} updated',
      created: 'Access role {{name}} created',
      deleted: 'Access role {{name}} deleted',
      nameImmutable:
        'Name is immutable after creation. Use a stable identifier and avoid embedding mutable subject lists in the name.',
      nameHint:
        '{{rule}} Use a stable identifier and avoid embedding mutable subject lists in the name.',
    },
    bindings: {
      cardTitle: 'Assignments',
      description:
        'Grant users and groups access roles for components, projects, and clusters. ADP applies the assigned permissions in connected systems.',
      showing: 'Showing {{value}} assignments',
      newBinding: 'New assignment',
      editBinding: 'Edit assignment',
      createBinding: 'Create assignment',
      saveBinding: 'Save assignment',
      deleteBinding: 'Delete assignment',
      statePhase: 'State',
      rbac: 'ADP Permissions',
      targetKind: 'Target Kind',
      targetRef: 'Target Ref',
      nameRequired: 'Name is required.',
      nameTooLong: 'Name must be no more than {{max}} characters.',
      namePattern: 'Name must be a lowercase RFC 1123 subdomain. {{rule}}',
      subjectRequired: 'Choose at least one user or group.',
      roleRequired: 'Choose an access role.',
      targetRequired: 'Choose a target before saving the assignment.',
      noProjectTargets: 'No projects related to this component are available.',
      subjectsHelpLoading:
        'Loading Backstage users, groups, components, clusters, and projects...',
      subjectsHelpReady:
        'Choose existing User and Group entities from the Backstage catalog.',
      invalidName: 'Assignment {{error}}',
      updated: 'Assignment {{name}} updated',
      created: 'Assignment {{name}} created',
      markedForDeletion:
        'Assignment {{name}} marked for deletion. Final cleanup will be completed by access synchronization.',
      nameImmutable:
        'Name is immutable after creation. Use a stable identifier and avoid embedding mutable subject lists in the name.',
      nameHint:
        '{{rule}} Use a stable identifier and avoid embedding mutable subject lists in the name.',
      generateName: 'Generate Name',
      nameGenerateHint:
        'Select at least one subject, an access role, and a target to generate a readable assignment name.',
      generateNameTooltip:
        'Available after selecting subjects, role, and target.',
      filterSearch: 'Search',
      filterAll: 'all',
      filterGroupBy: 'Group by',
      filterGroupNone: 'none',
      filterGroupStatus: 'status',
      filterGroupRole: 'role',
      filterGroupTarget: 'target',
      summaryTotals: '{{showing}} · total: {{total}} · failed: {{failed}}',
      groupTotals: '{{label}} (all: {{total}} · failed: {{failed}})',
      failedOnly: 'Failed only',
      clearFilters: 'Clear filters',
    },
    audit: {
      title: 'Audit Log',
      description:
        'View the history of access role, assignment, and manual permission changes.',
      viewPayloadAria: 'View audit payload for {{name}}',
      payloadDialogTitle: 'Audit Payload: {{kind}} {{name}}',
      markedForDeletion: 'Marked for deletion',
      created: 'Created',
      updated: 'Updated',
      deleted: 'Deleted',
      filterSearch: 'Search all fields',
      filterSearchPlaceholder: 'name / user / role / payload',
      searchSection: 'Search',
      filtersSection: 'Filters',
      filterKind: 'Resource type',
      filterAction: 'Action',
      filterAll: 'all',
      searchButton: 'Search',
      clearFilters: 'Clear filters',
    },
    auditPayload: {
      markedForDeletionDiff: 'Marked for deletion diff',
      changeDiff: 'Change diff',
      createdSnapshot: 'Created snapshot',
      deletedSnapshot: 'Deleted snapshot',
      payloadSnapshot: 'Payload snapshot',
      payload: 'Payload',
      noteMarkedForDeletion:
        'For assignment deletion, the popup shows the user action that moved the assignment into deleting state, not the later synchronization cleanup.',
      prettyDiffFallback:
        'Pretty diff modules failed to load. Showing the before and after snapshots instead.',
      syntaxFallback:
        'Syntax highlighting modules did not load, so the popup switched to a lightweight fallback.',
      showAllLines: 'Showing all lines, including unchanged context.',
      collapseUnchanged:
        'Unchanged blocks are collapsed to keep the interesting parts visible.',
      hideUnchanged: 'Hide unchanged',
      showUnchanged: 'Show unchanged',
      diffTableAria: 'Audit payload diff',
      delta: 'Delta',
      json: 'JSON',
      unchangedHidden: '{{value}} unchanged lines hidden',
      addedLines: '+{{value}} lines',
      removedLines: '-{{value}} lines',
    },
    reconciler: {
      runsTitle: 'Runs',
      lastStatusesTitle: 'Last Access Synchronization Statuses',
      materializationsTitle: 'Materializations',
      trackedResources: 'tracked resources',
      derivedState: 'derived state',
      activeMaterializationsCaption:
        'Currently active derived RBAC materializations',
      failedRunsMeta: '{{value}} failed or aborted runs on current page',
      noRunsYet: 'No access synchronization runs found yet',
      waitingFirstRun: 'Waiting for the first scheduler run',
      totalRuns: 'total {{value}} runs',
      startedAt: 'started {{value}}',
      pending: 'pending {{value}}',
      reconciling: 'reconciling {{value}}',
      ready: 'ready {{value}}',
      failedDeleting:
        'failed {{failed}} · deleting {{deleting}} · delete_failed {{deleteFailed}}',
      statusesMeta: 'Latest statuses recorded by access synchronization',
      triggerChanges: 'trigger: {{trigger}} · changes: {{mode}}',
      idle: 'idle',
      never: 'never',
      liveUpdatesOn: 'Live updates on',
      liveUpdatesOff: 'Live updates off',
      refreshing: 'Refreshing…',
      reconcileRuns: 'Access Synchronization Runs',
      reconcileRunsDescription:
        'History of scheduled and manual access synchronization runs.',
      reconcileRunsPaging:
        'Server-side paging remains enabled: 20 runs per request.',
      resourceStates: 'Resource States',
      resourceStatesDescription:
        'Current snapshot from `access_reconcile_resource_states`.',
      materializationsDescription:
        'Current contents of `access_reconcile_materializations`.',
      viewMaterializationsAria: 'View current materializations',
      run: 'Run',
      status: 'Status',
      trigger: 'Trigger',
      triggerManual: 'Manual',
      triggerScheduled: 'Scheduled',
      dryRun: 'Dry Run',
      stats: 'Stats',
      lastRun: 'Last Run',
      lastReconciled: 'Last Synchronized',
      deletionRequested: 'Deletion Requested',
      drawerTitle: 'Access Synchronization Run {{id}}',
      runResourceStates: 'Run Resource States',
      relatedMaterializations: 'Related Materializations',
      journal: 'Journal',
      modeApply: 'apply',
      modeDryRun: 'dry-run',
      milliseconds: '{{value}} ms',
      seconds: '{{value}} s',
      completedWithIssues: 'Completed with issues',
      taskFailures: 'task failures {{value}}',
      statsValue:
        'assignments {{bindings}}, roles {{roles}}, users {{users}}, +{{created}}, ~{{updated}}, -{{deleted}}, skip {{skipped}}, fail {{failed}}',
      taskStatsValue:
        '{{task}} → assignments {{bindings}}, roles {{roles}}, users {{users}}, +{{created}}, ~{{updated}}, -{{deleted}}, skip {{skipped}}, fail {{failed}}',
      overviewStatusCreated: 'Created: {{value}}',
      overviewStatusStarted: 'Started: {{value}}',
      overviewStatusFinished: 'Finished: {{value}}',
      overviewStatusDuration: 'Duration: {{value}}',
      overviewStatusStats: 'Stats: {{value}}',
      overviewStatusMessage: 'Message: {{value}}',
      messageManualRequested: 'Manual access synchronization requested',
      messageScheduledRequested: 'Scheduled access synchronization requested',
      messageFinishedSuccessfully:
        'Access synchronization finished successfully',
      messageAborted: 'Access synchronization aborted',
      messageFailed: 'Access synchronization failed',
    },
    status: {
      pending: 'Pending',
      ready: 'Ready',
      completed: 'Completed',
      reconciling: 'Reconciling',
      deleting: 'Deleting',
      running: 'Running',
      accepted: 'Accepted',
      failed: 'Failed',
      delete_failed: 'Delete failed',
      aborted: 'Aborted',
    },
    options: {
      guest: 'Guest',
      reporter: 'Reporter',
      developer: 'Developer',
      administrator: 'Administrator',
      owner: 'Owner',
      member: 'Member',
      admin: 'Admin',
      viewer: 'Viewer',
      component: 'Component',
      cluster: 'Cluster',
      project: 'Project',
    },
  },
  translations: {},
});
